import { CheckCircle as CheckCircleIcon, Clear as ClearIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import { styled } from '@mui/material/styles'
import { hasUserRight } from '@src/contexts/AppContext'
import { EUserRights } from '@src/data/types/Constants'
import { MerchantDashboardUser } from '@src/data/types/MerchantDashboardUserSchema'
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TableToolbar from '../components/toolBar'
import { formatDateTime } from '../containers/App/utils/Formatter'
import Footer from '../containers/ViewMerchant/components/footer'
import EditProfileDialog from '../containers/ViewMerchant/components/profileDialog'
import DeleteUserDialog from './deleteUserDialog'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

interface Props {
  merchantId: string
  usersList: MerchantDashboardUser[]
  isPendingSearchUsers: boolean
}

const Users = ({ merchantId, usersList, isPendingSearchUsers }: Props) => {
  const { t } = useTranslation()
  const [editUser, setEditUser] = useState<MerchantDashboardUser | undefined>(undefined)
  const [openUserDialog, setOpenUserDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [emailToDelete, setEmailToDelete] = useState('')
  const [filteredValue, setFilteredValue] = useState<string>('')

  const canDeleteUser = hasUserRight(EUserRights.canDeleteUser)

  const usersFiltered = useMemo(() => {
    if (!usersList) return []

    return usersList.filter(
      (user) =>
        user.email.toLowerCase().includes(filteredValue.toLowerCase()) ||
        user.firstName.toLowerCase().includes(filteredValue.toLowerCase()) ||
        user.lastName.toLowerCase().includes(filteredValue.toLowerCase()),
    )
  }, [usersList, filteredValue])

  const handleSearch = useCallback((value: string) => {
    setFilteredValue(value)
  }, [])

  const handleCloseProfile = () => {
    setEditUser(undefined)
    setOpenUserDialog(false)
  }

  const handleOpenDeleteDialog = useCallback((email: string) => {
    setOpenDeleteDialog(true)
    setEmailToDelete(email)
  }, [])

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false)
  }, [])

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const lastPage = rowsPerPage > 0 ? Math.max(0, Math.ceil(usersFiltered.length / rowsPerPage) - 1) : 0
  const safePage = Math.min(page, lastPage)
  if (safePage !== page) setPage(safePage)

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = safePage > 0 ? Math.max(0, (1 + safePage) * rowsPerPage - usersFiltered.length) : 0

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <>
      <TableContainer component={Paper}>
        <TableToolbar
          handleAdd={setOpenUserDialog}
          handleSearch={handleSearch}
          title={t('common.user')}
          addButtonTooltip={t('profile.addUser')}
          disableAddButton={false}
        />
        <Table sx={{ maxHeight: '25rem' }} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t('common.fullName')}</StyledTableCell>
              <StyledTableCell>{t('common.state')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.email')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.language')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.jobTitle')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.lastConnection')}</StyledTableCell>
              <StyledTableCell align="right" />
            </TableRow>
          </TableHead>

          <TableBody>
            {isPendingSearchUsers ? (
              <TableRow>
                <TableCell colSpan={20} style={{ padding: 0 }}>
                  <LinearProgress style={{ height: '10px' }} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {(!usersFiltered || usersFiltered.length === 0) && !isPendingSearchUsers && (
                  <TableRow>
                    <TableCell colSpan={20} sx={{ textAlign: 'center' }}>
                      {t('common.noResultsFound')}
                    </TableCell>
                  </TableRow>
                )}
                {(rowsPerPage > 0
                  ? usersFiltered.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
                  : usersFiltered
                ).map((row) => (
                  <TableRow key={row.email}>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant="body2"
                        component={Button}
                        onClick={() => {
                          setEditUser(row)
                        }}
                        color="info"
                      >
                        {row.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell align="left" sx={{ paddingLeft: '3.5%' }}>
                      {row.accountEnabled ? (
                        <Tooltip title={t('common.enable')}>
                          <CheckCircleIcon sx={{ color: 'green' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('common.disable')}>
                          <ClearIcon sx={{ color: 'red' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">{row.email}</TableCell>
                    <TableCell align="right">{row.language}</TableCell>
                    <TableCell align="right">{row.jobTitle}</TableCell>
                    <TableCell align="right">{formatDateTime(row.lastConnection)}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={canDeleteUser ? t('deleteDialog.dialogTitle') : t('deleteDialog.accessDeniedMessage')}
                      >
                        <span>
                          <IconButton
                            onClick={() => {
                              handleOpenDeleteDialog(row.email)
                            }}
                            disabled={!canDeleteUser}
                          >
                            <DeleteIcon
                              sx={[
                                canDeleteUser
                                  ? {
                                      color: 'red',
                                    }
                                  : {
                                      color: 'grey',
                                    },
                              ]}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                count={usersFiltered.length}
                rowsPerPage={rowsPerPage}
                page={safePage}
                slotProps={{
                  select: {
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  },
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={Footer}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <EditProfileDialog
        key={editUser?.email}
        open={openUserDialog || editUser !== undefined}
        handleClose={handleCloseProfile}
        user={editUser}
        merchantId={merchantId}
      />
      <DeleteUserDialog
        open={openDeleteDialog}
        handleClose={handleCloseDeleteDialog}
        email={emailToDelete}
        merchantId={merchantId}
      />
    </>
  )
}

export default Users
